import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Upload, Image as ImageIcon, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function resolveImg(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url}`;
}

const empty = { title: "", description: "", order: 0, is_active: true, image: null, image_preview: null };

export default function DispatcherServices() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // service object or null
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/services/all").then((r) => setServices(r.data)).catch(() => api.get("/services").then((r) => setServices(r.data)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      title: s.title, description: s.description, order: s.order, is_active: s.is_active,
      image: null, image_preview: resolveImg(s.image_url),
    });
    setOpen(true);
  };

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max image size is 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: file, image_preview: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("order", String(form.order || 0));
      fd.append("is_active", String(!!form.is_active));
      if (form.image) fd.append("image", form.image);

      const url = editing ? `/services/${editing.id}` : "/services";
      const method = editing ? "put" : "post";
      await api[method](url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(editing ? "Service updated" : "Service created");
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    try { await api.delete(`/services/${s.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleActive = async (s) => {
    try {
      const fd = new FormData();
      fd.append("is_active", String(!s.is_active));
      await api.put(`/services/${s.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="dispatcher-services">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>Services Manager</h1>
          <p className="text-slate-500 mt-1">Edit the service cards shown on the public landing page. Upload your own images and write-ups.</p>
        </div>
        <Button onClick={openNew} className="bg-brand-navy hover:bg-brand-blue" data-testid="svc-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New service
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => {
          const img = resolveImg(s.image_url);
          return (
            <Card key={s.id} className="overflow-hidden hover:border-brand-teal transition-colors" data-testid={`svc-card-${s.id}`}>
              <div className="relative h-40 bg-slate-100">
                {img ? (
                  <img src={img} alt={s.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-10 h-10 text-slate-300" /></div>
                )}
                {!s.is_active && (
                  <div className="absolute top-2 right-2 bg-slate-900/80 text-white text-[10px] px-2 py-1 rounded-full font-semibold">Hidden</div>
                )}
                <div className="absolute top-2 left-2 bg-white/95 text-brand-navy text-[10px] px-2 py-1 rounded-full font-bold">#{s.order || 0}</div>
              </div>
              <CardContent className="p-4">
                <div className="font-bold text-slate-900">{s.title}</div>
                <p className="mt-1 text-xs text-slate-600 line-clamp-3 whitespace-pre-line">{s.description}</p>
                <div className="mt-4 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`svc-edit-${s.id}`}>
                    <Edit className="w-3.5 h-3.5 mr-1" />Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleActive(s)} data-testid={`svc-toggle-${s.id}`}>
                    {s.is_active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto" onClick={() => remove(s)} data-testid={`svc-delete-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {services.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500">No services yet. Click <strong>New service</strong> to create one.</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit service" : "Create service"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Image</Label>
              <div className="mt-1 border-2 border-dashed border-slate-200 rounded-xl p-4 flex items-center gap-4">
                {form.image_preview ? (
                  <img src={form.image_preview} alt="preview" className="w-28 h-20 object-cover rounded-lg" />
                ) : (
                  <div className="w-28 h-20 bg-slate-50 rounded-lg flex items-center justify-center text-slate-300">
                    <ImageIcon className="w-8 h-8" />
                  </div>
                )}
                <div className="flex-1">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pickImage} data-testid="svc-image-input" />
                    <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-navy text-white text-sm font-semibold hover:bg-brand-blue">
                      <Upload className="w-4 h-4" /> {form.image_preview ? "Replace image" : "Upload image"}
                    </span>
                  </label>
                  <div className="text-xs text-slate-500 mt-2">PNG, JPG, WEBP up to 5MB. Recommended 1200×800.</div>
                </div>
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="svc-title-input" />
            </div>
            <div>
              <Label>Description / write-up</Label>
              <Textarea
                rows={5}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe this service in detail. Line breaks are preserved."
                data-testid="svc-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <Label>Display order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} data-testid="svc-order-input" />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Visible</div>
                  <div className="text-xs text-slate-500">Show on landing</div>
                </div>
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="svc-active-switch" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="svc-cancel-btn">Cancel</Button>
            <Button disabled={saving} onClick={save} className="bg-brand-navy hover:bg-brand-blue" data-testid="svc-save-btn">
              {saving ? "Saving…" : (editing ? "Save changes" : "Create service")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
